import {
	AutosaveErrorCode,
	type AutosaveDraftSnapshotContract,
	type AutosaveScopeContract,
	type AutosaveUpsertResponseContract,
} from '@workspace/shared-constants';
import React from 'react';
import { useTranslation } from 'react-i18next';

import PopupNotification from '@/domain/core/PopupNotification';

import { autosaveApiService } from '../api/AutosaveApiService';
import { stableStringify } from '../utils/stableStringify';

type AutosaveSource = 'autosave' | 'baseline' | 'empty';

type AutosaveState = {
	activeSource: AutosaveSource;
	canSwitchSource: boolean;
	currentRevision: number;
	draftUpdatedAt: string | null;
	draftVersion: number | null;
	firstEditLocked: boolean;
	hasUnsavedChanges: boolean;
	isDirty: boolean;
	isSaving: boolean;
	lastAutosavedFingerprint: string | null;
	lastAutosavedRevision: number;
	restoreModalOpen: boolean;
};

type UseAutosaveControllerParams<TPayload> = AutosaveScopeContract & {
	allowEmptySource?: boolean;
	baselinePayload?: TPayload;
	enabled?: boolean;
	getPayload: () => TPayload;
	loadEmptyPayload?: () => TPayload;
	applyPayload: (payload: TPayload) => void;
};

type AvailableSources = {
	hasAutosaveSource: boolean;
	hasBaselineSource: boolean;
	hasEmptySource: boolean;
};

const AUTOSAVE_DEBOUNCE_MS = 30_000;
const AUTOSAVE_SAFEGUARD_MS = 5 * 60_000;

const initialState: AutosaveState = {
	activeSource: 'baseline',
	canSwitchSource: false,
	currentRevision: 0,
	draftUpdatedAt: null,
	draftVersion: null,
	firstEditLocked: false,
	hasUnsavedChanges: false,
	isDirty: false,
	isSaving: false,
	lastAutosavedFingerprint: null,
	lastAutosavedRevision: 0,
	restoreModalOpen: false,
};

const hasDraftScope = (scope: AutosaveScopeContract) => Boolean(scope.projectId || scope.selectionId);

export const useAutosaveController = <TPayload,>({
	allowEmptySource = false,
	baselinePayload,
	enabled = true,
	getPayload,
	loadEmptyPayload,
	applyPayload,
	...scope
}: UseAutosaveControllerParams<TPayload>) => {
	const { t } = useTranslation();
	const [state, setState] = React.useState<AutosaveState>(initialState);
	const [availableSources, setAvailableSources] = React.useState<AvailableSources>({
		hasAutosaveSource: false,
		hasBaselineSource: true,
		hasEmptySource: false,
	});

	const loadedScopeKeyRef = React.useRef<string | null>(null);
	const loadingScopeKeyRef = React.useRef<string | null>(null);
	const canSaveDraftRef = React.useRef(false);
	const isProgrammaticChangeRef = React.useRef(false);
	const baselinePayloadFingerprintRef = React.useRef<string | null>(null);
	const loadedDraftRef = React.useRef<AutosaveDraftSnapshotContract<TPayload> | null>(null);
	const emptySourcePayloadRef = React.useRef<TPayload | null>(null);
	const savingFingerprintRef = React.useRef<string | null>(null);
	const isMountedRef = React.useRef(true);
	const currentScopeKeyRef = React.useRef<string | null>(null);
	const getEmptyPayloadRef = React.useRef(loadEmptyPayload);
	const pendingSourceFingerprintRef = React.useRef<string | null>(null);
	const saveRequestIdRef = React.useRef(0);
	const pendingAutosaveTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const draftScope = React.useMemo(
		() => ({
			entityId: scope.entityId ?? null,
			entityType: scope.entityType,
			projectId: scope.projectId ?? null,
			selectionId: scope.selectionId ?? null,
		}),
		[scope.entityId, scope.entityType, scope.projectId, scope.selectionId]
	);

	const draftScopeKey = React.useMemo(
		() =>
			JSON.stringify([
				draftScope.entityType,
				draftScope.projectId,
				draftScope.selectionId,
				draftScope.entityId,
			]),
		[draftScope]
	);

	const baselinePayloadFingerprint = React.useMemo(
		() => (baselinePayload === undefined ? null : stableStringify(baselinePayload)),
		[baselinePayload]
	);

	const clearPendingAutosave = React.useCallback(() => {
		if (pendingAutosaveTimeoutRef.current) {
			clearTimeout(pendingAutosaveTimeoutRef.current);
			pendingAutosaveTimeoutRef.current = null;
		}
	}, []);

	const getCurrentPayloadFingerprint = React.useCallback(() => {
		return stableStringify(getPayload());
	}, [getPayload]);

	const updateFormFromSource = React.useCallback(
		(payload: TPayload, source: AutosaveSource) => {
			canSaveDraftRef.current = true;
			isProgrammaticChangeRef.current = true;
			pendingSourceFingerprintRef.current = stableStringify(payload);
			applyPayload(payload);

			setTimeout(() => {
				isProgrammaticChangeRef.current = false;
			}, 0);

			setState((prev) => ({
				...prev,
				activeSource: source,
				canSwitchSource:
					prev.firstEditLocked ? false : availableSources.hasAutosaveSource || allowEmptySource,
				firstEditLocked: false,
				hasUnsavedChanges: false,
				isDirty: false,
				restoreModalOpen: false,
			}));
		},
		[allowEmptySource, applyPayload, availableSources.hasAutosaveSource]
	);

	const updateStateAfterAutosave = React.useCallback(
		(response: AutosaveUpsertResponseContract, payloadFingerprint: string, revision: number) => {
			setState((prev) => ({
				...prev,
				draftUpdatedAt: response.updatedAt,
				draftVersion: response.version,
				hasUnsavedChanges: false,
				isDirty: false,
				isSaving: false,
				lastAutosavedFingerprint: payloadFingerprint,
				lastAutosavedRevision: revision,
			}));
		},
		[]
	);

	const saveDraftIfNeeded = React.useCallback(async () => {
		if (
			!enabled ||
			!hasDraftScope(draftScope) ||
			!loadedScopeKeyRef.current ||
			!canSaveDraftRef.current ||
			!state.hasUnsavedChanges
		) {
			return;
		}

		const payloadFingerprint = getCurrentPayloadFingerprint();

		if (payloadFingerprint === state.lastAutosavedFingerprint) {
			return;
		}

		if (payloadFingerprint === savingFingerprintRef.current) {
			return;
		}

		const revision = state.currentRevision;
		const requestId = ++saveRequestIdRef.current;

		savingFingerprintRef.current = payloadFingerprint;
		setState((prev) => ({ ...prev, isSaving: true }));

		try {
			const response = await autosaveApiService.saveDraft({
				...draftScope,
				payload: getPayload(),
				knownVersion: state.draftVersion ?? undefined,
			});

			if (requestId !== saveRequestIdRef.current) {
				return;
			}

			if (payloadFingerprint !== getCurrentPayloadFingerprint()) {
				setState((prev) => ({ ...prev, isSaving: false }));
				return;
			}

			updateStateAfterAutosave(response, payloadFingerprint, revision);
		} catch (error: any) {
			if (requestId !== saveRequestIdRef.current) {
				return;
			}

			setState((prev) => ({ ...prev, isSaving: false }));

			const errorCode = error?.response?.data?.code as AutosaveErrorCode | undefined;

			if (errorCode === AutosaveErrorCode.VERSION_CONFLICT) {
				PopupNotification.emitter('error', t('autosave.error.conflict'));
				console.error('Autosave version conflict', error?.response?.data ?? error);
				return;
			}

			PopupNotification.emitter('error', t('autosave.error.generic'));
			console.error('Autosave failed', error?.response?.data ?? error);
		} finally {
			if (savingFingerprintRef.current === payloadFingerprint) {
				savingFingerprintRef.current = null;
			}
		}
	}, [
		draftScope,
		enabled,
		getCurrentPayloadFingerprint,
		getPayload,
		state.currentRevision,
		state.draftVersion,
		state.hasUnsavedChanges,
		state.lastAutosavedFingerprint,
		t,
		updateStateAfterAutosave,
	]);

	const updateFromUserInput = React.useCallback((nextPayload?: TPayload) => {
		if (!canSaveDraftRef.current) {
			return;
		}

		if (nextPayload !== undefined && pendingSourceFingerprintRef.current) {
			const nextPayloadFingerprint = stableStringify(nextPayload);

			if (nextPayloadFingerprint === pendingSourceFingerprintRef.current) {
				return;
			}

			pendingSourceFingerprintRef.current = null;
		}

		if (isProgrammaticChangeRef.current) {
			return;
		}

		setState((prev) => ({
			...prev,
			canSwitchSource: false,
			currentRevision: prev.currentRevision + 1,
			firstEditLocked: true,
			hasUnsavedChanges: true,
			isDirty: true,
		}));
	}, []);

	const useBaselineVersion = React.useCallback(() => {
		if (baselinePayload === undefined) {
			return;
		}

		updateFormFromSource(baselinePayload, 'baseline');
	}, [baselinePayload, updateFormFromSource]);

	const useAutosavedVersion = React.useCallback(() => {
		if (!loadedDraftRef.current) {
			return;
		}

		updateFormFromSource(loadedDraftRef.current.payload as TPayload, 'autosave');

		setState((prev) => ({
			...prev,
			draftUpdatedAt: loadedDraftRef.current?.updatedAt ?? prev.draftUpdatedAt,
			draftVersion: loadedDraftRef.current?.version ?? prev.draftVersion,
			lastAutosavedFingerprint: loadedDraftRef.current
				? stableStringify(loadedDraftRef.current.payload)
				: prev.lastAutosavedFingerprint,
			lastAutosavedRevision: prev.currentRevision,
		}));
	}, [updateFormFromSource]);

	const useEmptyVersion = React.useCallback(() => {
		if (!allowEmptySource || !loadEmptyPayload) {
			return;
		}

		const emptyPayload = loadEmptyPayload();
		emptySourcePayloadRef.current = emptyPayload;
		updateFormFromSource(emptyPayload, 'empty');
	}, [allowEmptySource, loadEmptyPayload, updateFormFromSource]);

	const changeSource = React.useCallback(
		(source: AutosaveSource) => {
			if (!state.canSwitchSource) {
				return;
			}

			if (source === 'autosave') {
				useAutosavedVersion();
				return;
			}

			if (source === 'empty') {
				useEmptyVersion();
				return;
			}

			useBaselineVersion();
		},
		[state.canSwitchSource, useAutosavedVersion, useBaselineVersion, useEmptyVersion]
	);

	const clearDraftAfterMainSave = React.useCallback(async () => {
		canSaveDraftRef.current = true;
		const currentPayloadFingerprint = getCurrentPayloadFingerprint();

		setState((prev) => ({
			...prev,
			canSwitchSource: false,
			draftUpdatedAt: null,
			draftVersion: null,
			firstEditLocked: true,
			hasUnsavedChanges: false,
			isDirty: false,
			isSaving: false,
			lastAutosavedFingerprint: currentPayloadFingerprint,
			lastAutosavedRevision: prev.currentRevision,
			restoreModalOpen: false,
		}));

		loadedDraftRef.current = null;
		pendingSourceFingerprintRef.current = null;
		setAvailableSources((prev) => ({ ...prev, hasAutosaveSource: false }));

		try {
			await autosaveApiService.deleteDraft(draftScope);
		} catch (error) {
			console.error('Autosave cleanup request failed', error);
		}
	}, [draftScope, getCurrentPayloadFingerprint]);

	React.useEffect(() => {
		return () => clearPendingAutosave();
	}, [clearPendingAutosave]);

	React.useEffect(() => {
		currentScopeKeyRef.current = draftScopeKey;
	}, [draftScopeKey]);

	React.useEffect(() => {
		getEmptyPayloadRef.current = loadEmptyPayload;
	}, [loadEmptyPayload]);

	React.useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	React.useEffect(() => {
		loadedScopeKeyRef.current = null;
		loadingScopeKeyRef.current = null;
		canSaveDraftRef.current = false;
		loadedDraftRef.current = null;
		emptySourcePayloadRef.current = null;
		savingFingerprintRef.current = null;
		pendingSourceFingerprintRef.current = null;
		saveRequestIdRef.current = 0;
		baselinePayloadFingerprintRef.current = null;
		setState(initialState);
		setAvailableSources({
			hasAutosaveSource: false,
			hasBaselineSource: true,
			hasEmptySource: false,
		});
	}, [draftScopeKey]);

	React.useEffect(() => {
		if (!enabled || !hasDraftScope(draftScope) || baselinePayloadFingerprint === null) {
			return;
		}

		baselinePayloadFingerprintRef.current = baselinePayloadFingerprint;
		emptySourcePayloadRef.current =
			allowEmptySource && getEmptyPayloadRef.current ? getEmptyPayloadRef.current() : null;

		if (
			loadedScopeKeyRef.current === draftScopeKey ||
			loadingScopeKeyRef.current === draftScopeKey
		) {
			return;
		}

		const requestedScopeKey = draftScopeKey;
		const requestedScope = draftScope;
		loadingScopeKeyRef.current = requestedScopeKey;

		const fetchDraft = async () => {
			try {
				const response = await autosaveApiService.fetchDraft<TPayload>(requestedScope);

				if (!isMountedRef.current || currentScopeKeyRef.current !== requestedScopeKey) {
					return;
				}

				loadedScopeKeyRef.current = requestedScopeKey;

				const loadedDraft = response.draft;
				const draftPayloadFingerprint = loadedDraft ? stableStringify(loadedDraft.payload) : null;
				const shouldOpenRestoreModal = Boolean(
					loadedDraft && draftPayloadFingerprint !== baselinePayloadFingerprintRef.current
				);

				loadedDraftRef.current = loadedDraft;
				canSaveDraftRef.current = !shouldOpenRestoreModal;

				setState((prev) => ({
					...prev,
					activeSource: 'baseline',
					draftUpdatedAt: loadedDraft?.updatedAt ?? null,
					draftVersion: loadedDraft?.version ?? null,
					lastAutosavedFingerprint: draftPayloadFingerprint,
					restoreModalOpen: shouldOpenRestoreModal,
				}));

				setAvailableSources({
					hasAutosaveSource: Boolean(loadedDraft),
					hasBaselineSource: true,
					hasEmptySource: Boolean(emptySourcePayloadRef.current),
				});
			} catch (error) {
				if (!isMountedRef.current || currentScopeKeyRef.current !== requestedScopeKey) {
					return;
				}

				console.error('Autosave initialization failed', error);
				loadedScopeKeyRef.current = requestedScopeKey;
				canSaveDraftRef.current = true;
			} finally {
				if (loadingScopeKeyRef.current === requestedScopeKey) {
					loadingScopeKeyRef.current = null;
				}
			}
		};

		fetchDraft().catch(() => undefined);
	}, [allowEmptySource, baselinePayloadFingerprint, draftScope, draftScopeKey, enabled]);

	React.useEffect(() => {
		if (!enabled || !state.hasUnsavedChanges) {
			clearPendingAutosave();
			return;
		}

		clearPendingAutosave();
		pendingAutosaveTimeoutRef.current = setTimeout(() => {
			saveDraftIfNeeded().catch(() => undefined);
		}, AUTOSAVE_DEBOUNCE_MS);

		return () => clearPendingAutosave();
	}, [clearPendingAutosave, enabled, saveDraftIfNeeded, state.currentRevision, state.hasUnsavedChanges]);

	React.useEffect(() => {
		if (!enabled) {
			return;
		}

		const autosaveInterval = setInterval(() => {
			if (!state.hasUnsavedChanges) {
				return;
			}

			saveDraftIfNeeded().catch(() => undefined);
		}, AUTOSAVE_SAFEGUARD_MS);

		return () => clearInterval(autosaveInterval);
	}, [enabled, saveDraftIfNeeded, state.hasUnsavedChanges]);

	return {
		state,
		availableSources,
		updateFromUserInput,
		changeSource,
		useAutosavedVersion,
		useBaselineVersion,
		useEmptyVersion,
		clearDraftAfterMainSave,
	};
};
